import { useCallback, useState } from "react";

const STORAGE_KEY = "nubrakes:schedule-view:v1";

export type ScheduleView = "calendar" | "list";

const isScheduleView = (value: unknown): value is ScheduleView => value === "calendar" || value === "list";

/**
 * Which schedule tab the technician last used, remembered across reloads.
 *
 * A technician works one way — the grid or the list — and re-picking it after every
 * refresh is friction. Validated on read rather than cast: a stale or hand-edited value
 * must not leave MUI's Tabs with a value no Tab matches. localStorage can throw in a
 * locked-down browser, in which case this quietly behaves as an unremembered default.
 */
const read = (): ScheduleView | null => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isScheduleView(stored) ? stored : null;
  } catch {
    return null;
  }
};

const write = (value: ScheduleView) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Nothing to do — the choice still holds for this page load.
  }
};

export const useScheduleView = (): [ScheduleView, (next: ScheduleView) => void] => {
  const [view, setView] = useState<ScheduleView>(() => read() ?? "calendar");

  const select = useCallback((next: ScheduleView) => {
    setView(next);
    write(next);
  }, []);

  return [view, select];
};
